import React, { useState, useEffect, useCallback, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faIndianRupeeSign,
  faWallet,
  faCreditCard,
  faCoins,
  faGauge,
} from '@fortawesome/free-solid-svg-icons';
import { ACCOUNT_TYPES, MODULES, PERMISSION_TYPES } from '../../constants';
import { authData, formatCurrency } from '../../utils';

/* =========================
   Animations
========================= */
const slideDown = keyframes`
  0% { opacity: 0; transform: translateY(-10px);}
  100% { opacity: 1; transform: translateY(0);}
`;

/* =========================
   Styled Components
========================= */
const WalletContainer = styled.section`
  width: 60px;
  position: relative;
  border-left: 1px solid #e5e5e5;
  @media (max-width: 600px) {
    width: auto;
  }
`;

const WalletIconButton = styled.button`
  width: 100%;
  height: 100%;
  background: transparent;
  border: none;
  cursor: pointer;
  outline: none;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: 0.3s all;

  svg {
    font-size: 22px;
    color: #707070;
    transition: 0.3s all;
  }

  &:hover svg,
  &.active svg {
    color: #fff !important; // ensures icon stays visible
  }

  &:hover {
    background-color: ${({ theme }) => theme.colors.secondaryColor || '#0078ce'};
    border-radius: 8px;
  }

  &.active {
    background-color: ${({ theme }) => theme.colors.secondaryColor || '#0078ce'};
    border-radius: 8px;
  }
`;


const WalletDropdown = styled.div`
  position: absolute;
  top: 55px;
  right: 0;
  width: 320px;
  background: #fff;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
  border-radius: 10px;
  animation: ${slideDown} 0.25s ease-out;
  z-index: 10;

  @media (max-width: 600px) {
    width: 100%;
    right: 0;
  }
`;

const WalletHeader = styled.div`
  background-color: ${({ theme }) => theme.colors.secondaryColor || '#0078ce'};
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  padding: 12px 15px;

  h3 {
    color: #fff;
    font-weight: 600;
    font-size: 14px;
    margin: 0;
  }
`;

const WalletCard = styled.div`
  display: flex;
  align-items: center;
  padding: 15px;
  background: ${({ bgColor }) => bgColor || '#f8f9fa'};
  margin: 10px 15px;
  border-radius: 10px;
  box-shadow: 0px 2px 6px rgba(0,0,0,0.05);
  transition: 0.3s all;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0px 4px 12px rgba(0,0,0,0.1);
  }

  svg {
    font-size: 28px;
    color: ${({ iconColor }) => iconColor || '#228b22'};
    margin-right: 15px;
  }

  div {
    display: flex;
    flex-direction: column;

    span {
      font-size: 12px;
      color: #6b7280;
    }

    h4 {
      font-size: 18px;
      margin: 2px 0 0 0;
      font-weight: 600;
    }
  }
`;

const WalletLinks = styled.div`
  margin: 15px;

  a {
    display: flex;
    align-items: center;
    padding: 12px;
    text-decoration: none;
    color: #333;
    border-radius: 8px;
    transition: 0.3s all;
    
    svg {
      margin-right: 10px;
      font-size: 18px;
      color: ${({ theme }) => theme.colors.primaryColor || '#0095ff'};
    }

    &:hover,
    &.active {
      background-color: #f1f1f1;
    }
  }
`;

/* =========================
   Component
========================= */
const WalletSection = ({
  walletBalance,
  creditDue,
  minimumBalance,
  commissionMode,
  commissionValue,
}) => {
  const { accountType, accountsPermissions } = authData.get();
  const [expandWallet, setExpandWallet] = useState(false);
  const walletRef = useRef(null);

  const toggleWallet = (e) => {
    e.preventDefault();
    setExpandWallet((prev) => !prev);
  };

  const closeWallet = useCallback(
    (e) => {
      if (walletRef.current && !walletRef.current.contains(e.target)) {
        setExpandWallet(false);
      }
    },
    [walletRef]
  );

  useEffect(() => {
    document.addEventListener('click', closeWallet, true);
    return () => document.removeEventListener('click', closeWallet, true);
  }, [closeWallet]);

  const canReadWalletTransactions =
    accountsPermissions?.[MODULES.WALLET_TRANSACTIONS]?.includes(PERMISSION_TYPES.READ) ||
    false;

  return (
    <WalletContainer ref={walletRef}>
      <WalletIconButton className={expandWallet ? 'active' : ''} onClick={toggleWallet}>
        <FontAwesomeIcon icon={faWallet} />
      </WalletIconButton>

      {expandWallet && (
        <WalletDropdown>
          <WalletHeader>
            <h3>Wallet Overview</h3>
          </WalletHeader>

          {[ACCOUNT_TYPES.MASTER_DISTRIBUTORS, ACCOUNT_TYPES.DISTRIBUTORS, ACCOUNT_TYPES.RETAILERS, ACCOUNT_TYPES.API_USERS].includes(accountType) && (
            <WalletCard iconColor="#228b22">
              <FontAwesomeIcon icon={faIndianRupeeSign} />
              <div>
                <span>Balance</span>
                <h4>{formatCurrency(walletBalance)}</h4>
              </div>
            </WalletCard>
          )}

          <WalletCard iconColor="#f59e0b">
            <FontAwesomeIcon icon={faCreditCard} />
            <div>
              <span>Credit Due</span>
              <h4>{formatCurrency(creditDue)}</h4>
            </div>
          </WalletCard>

          {/* <WalletCard iconColor="#ef4444">
            <FontAwesomeIcon icon={faGauge} />
            <div>
              <span>Minimum Balance</span>
              <h4>{formatCurrency(minimumBalance)}</h4>
            </div>
          </WalletCard> */}

          <WalletCard iconColor="#10b981">
            <FontAwesomeIcon icon={faCoins} />
            <div>
              <span>Commission Type</span>
              <h4>{commissionMode === 'Flat' ? `${commissionValue}%` : commissionMode}</h4>
            </div>
          </WalletCard>

          {canReadWalletTransactions && (
            <WalletCard>
              <NavLink to="/wallet-transactions" className={(state) => (state.isActive ? 'active' : '')}>
                <FontAwesomeIcon icon={faWallet} />
                Wallet Transactions
              </NavLink>
            </WalletCard>
          )}
        </WalletDropdown>
      )}
    </WalletContainer>
  );
};

export default WalletSection;
